import { useEffect, useState, type FC } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import {
  addClientClarification,
  createClientCalendarEvent,
  createClientJointArticle,
  createClientNote,
  deleteClientCalendarEvent,
  deleteClientNote,
  getClientSummary,
  updateClientCalendarEvent,
  updateClientJointArticle,
  updateClientNote,
  updateClientProjectStage,
} from './api';
import type {
  ClientCardId,
  ClientOverviewStats,
  ClientProfile,
  ClientProjectStage,
  ClientWorkspaceSummary,
} from './types';
import {
  ArticleIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  ConnectionsIcon,
  FilesIcon,
  MessageSquareIcon,
  NotebookIcon,
  ProjectIcon,
  RequestIcon,
  ToolsIcon,
} from './components/ClientIcons';
import { ClientGrid } from './components/ClientGrid';
import { getWorkRequests, RoleMyRequestsOverlay, type WorkRequestListResponse } from '@/features/work-requests';
import { ClientRequestsOverlay } from './components/ClientRequestsOverlay';
import { ClientProjectsOverlay } from './components/ClientProjectsOverlay';
import { ClientCalendarOverlay } from './components/ClientCalendarOverlay';
import { ClientNotebookOverlay } from './components/ClientNotebookOverlay';
import { ClientArticlesOverlay } from './components/ClientArticlesOverlay';
import { SuperAiWorkspaceModal } from '@/features/super-ai';
import { CreateRequestEntry } from '@/features/request-gateway';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';

const CARD_PATHS: Record<ClientCardId | 'ARTICLES', string> = {
  MY_REQUESTS: 'requests',
  MY_PROJECTS: 'projects',
  ACADEMIC_CALENDAR: 'calendar',
  ACADEMIC_CONNECTIONS: 'connections',
  RESEARCH_TOOLS: 'tools',
  ACADEMIC_NOTEBOOK: 'notebook',
  B5_FILES: 'files',
  ARTICLES: 'articles',
};

export const ClientPage: FC = () => {
  const { user } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ClientWorkspaceSummary | null>(null);
  const [serviceRequests, setServiceRequests] = useState<WorkRequestListResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);
  const backToCabinet = () => navigate(routes.client);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const [data, requests] = await Promise.all([getClientSummary(), getWorkRequests({ role_context: 'CLIENT' })]);
      setSummary(data);
      setServiceRequests(requests);
    } catch {
      setSummary(null);
      setServiceRequests(null);
      setLoadError(true);
      notify('Не удалось загрузить кабинет клиента', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenCard = (id: ClientCardId | 'ARTICLES') => {
    const segment = CARD_PATHS[id];
    if (segment) navigate(segment);
  };

  const handleAddClarification = async (requestId: string, message: string) => {
    await addClientClarification(requestId, message);
    await loadData();
  };

  const handleUpdateStage = async (
    projectId: string,
    stageId: string,
    payload: Partial<ClientProjectStage>
  ) => {
    await updateClientProjectStage(projectId, stageId, payload);
    await loadData();
  };

  const previousStats: ClientOverviewStats = summary?.stats || {
    total_requests: 0,
    in_progress_requests: 0,
    completed_requests: 0,
    active_projects: 0,
    pending_deliverables: 0,
    upcoming_deadlines: 0,
    unread_messages: 0,
    total_files: 0,
    joint_articles_count: 0,
  };

  const stats: ClientOverviewStats = {
    ...previousStats,
    total_requests: previousStats.total_requests + (serviceRequests?.counts.total || 0),
    in_progress_requests: previousStats.in_progress_requests + (serviceRequests?.counts.in_progress || 0),
    completed_requests: previousStats.completed_requests + (serviceRequests?.counts.completed || 0),
  };

  const requestStatusLabels: Record<string, string> = { COMPLETED: 'Завершена', IN_PROGRESS: 'В работе', IN_REVIEW: 'На проверке', SUBMITTED: 'Подана', NEEDS_CLARIFICATION: 'На уточнении', DRAFT: 'Черновик', CANCELLED: 'Отменена', REJECTED: 'Отклонена' };
  const requestFeed = [
    ...(summary?.requests || []).map((req) => ({ id: req.id, code: req.request_number, title: req.title,
      status: req.status, statusLabel: requestStatusLabels[req.status] || 'Статус уточняется', created: req.created_at,
      deadline: req.target_deadline, budget: req.budget, ready: !!req.ready_result_path, previous: true })),
    ...(serviceRequests?.items || []).map((req) => ({ id: req.id, code: req.reference_code, title: req.title || req.service_name,
      status: req.status, statusLabel: req.status_label, created: req.created_at,
      deadline: req.due_at_label, budget: undefined, ready: req.status === 'RESULT_READY', previous: false })),
  ].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
  const totalRequests = stats.total_requests;

  const profile: ClientProfile = summary?.profile || {
    id: '',
    user_id: '',
    full_name: user ? `${user.firstName} ${user.lastName}` : 'Клиент Академии',
    email: user?.email || '',
    organization: 'ЕНУ им. Л.Н. Гумилева',
    position: 'Соискатель',
    academic_degree: 'Магистр наук',
  };

  if (isLoading && !summary) {
    return (
      <div className="cabinet-note" style={{ margin: '48px auto', maxWidth: 420, textAlign: 'center' }}>
        Загрузка кабинета клиента…
      </div>
    );
  }

  if (!summary || loadError) {
    return (
      <div className="cabinet-stack" style={{ margin: '48px auto', maxWidth: 420, textAlign: 'center' }}>
        <div className="cabinet-note danger">Не удалось загрузить данные кабинета</div>
        <button type="button" className="cabinet-btn primary" onClick={() => void loadData()}>
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="client-cabinet role-cabinet-page">
      <Routes>
      <Route
        index
        element={
      <CabinetWorkspaceFrame
        links={{
          notebook: `${routes.client}/notebook`,
          calendar: `${routes.client}/calendar`,
          meetings: `${routes.client}/calendar`,
          connections: `${routes.client}/connections`,
          alliances: `${routes.client}/articles`,
          qa: routes.portalSection('instructions'),
          support: routes.portalSection('contacts'),
        }}
      >
      <header className="client-hero role-hero" style={{ ['--role-accent' as string]: '#e2ad3a' }}>
        <div className="client-hero__glow client-hero__glow--a" />
        <div className="client-hero__glow client-hero__glow--b" />

        <div className="client-hero__body">
          <div className="client-hero__identity">
            <div className="client-hero__avatar">
              {profile.full_name
                ? profile.full_name
                    .split(' ')
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join('')
                    .toUpperCase()
                : 'КЛ'}
            </div>

            <div>
              <small>Кабинет заказчика</small>
              <h2>{profile.full_name || 'Клиент Академии'}</h2>
              <p>
                {profile.organization} · {profile.position}
              </p>
            </div>
          </div>

          <div className="client-hero__actions role-hero-actions">
            <button type="button" className="role-btn" onClick={() => setIsAIModalOpen(true)}>
              Супер AI-ассистент
            </button>
            <button type="button" className="role-btn primary" onClick={() => setIsCreateModalOpen(true)}>
              + Создать заявку
            </button>
          </div>
        </div>
      </header>

      <section className="client-kpi" aria-label="Статистика кабинета">
        {[
          { label: 'Всего заявок', value: totalRequests, accent: '#e2ad3a', icon: RequestIcon },
          { label: 'Заявок в работе', value: stats.in_progress_requests, accent: '#41d2ff', icon: ClockIcon },
          { label: 'Активных проектов', value: stats.active_projects, accent: '#49ddb7', icon: ProjectIcon },
          { label: 'Этапов на сдаче', value: stats.pending_deliverables, accent: '#5da8ff', icon: CheckCircleIcon },
          { label: 'Дедлайнов', value: stats.upcoming_deadlines, accent: '#ff7a64', icon: CalendarIcon },
          { label: 'Новых сообщений', value: stats.unread_messages, accent: '#9e80ff', icon: MessageSquareIcon },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <article key={item.label} style={{ ['--kpi-accent' as string]: item.accent }}>
              <div>
                <small>{item.label}</small>
                <Icon className="w-4 h-4" />
              </div>
              <b>{item.value}</b>
            </article>
          );
        })}
      </section>

      <section className="client-panel">
        <div className="client-section-head">
          <div>
            <h2>Рабочие разделы кабинета</h2>
            <small>7 модулей · единый аккаунт без повторного ввода</small>
          </div>
          <span className="client-pill client-pill--gold">Кабинет клиента</span>
        </div>
        <ClientGrid
          stats={stats}
          onSelectCard={handleOpenCard}
        />
      </section>

      <ScientificGpsBlock openTo={`${routes.client}/articles`} />

      <div className="client-bottom">
        <section className="client-panel min-w-0">
          <div className="client-section-head">
            <div className="client-section-head__title">
              <RequestIcon className="w-4 h-4 text-amber-400" />
              <h2>Лента активных обращений и заявок</h2>
            </div>
            <button type="button" className="client-link" onClick={() => handleOpenCard('MY_REQUESTS')}>
              Все заявки ({totalRequests}) →
            </button>
          </div>

          <div className="client-feed">
            {requestFeed.length > 0 ? (
              requestFeed.slice(0, 4).map((req) => (
                <button
                  key={req.id}
                  type="button"
                  className="client-feed__item"
                  onClick={() => navigate(req.previous ? `${routes.client}/requests/previous` : `${routes.client}/requests`)}
                >
                  <div className="min-w-0">
                    <div className="client-feed__meta">
                      <span className="client-feed__code">{req.code}</span>
                      <span
                        className={`client-chip ${
                          req.status === 'COMPLETED' ? 'is-done' : 'is-work'
                        }`}
                      >
                        {req.statusLabel}
                      </span>
                      {req.ready ? <span className="client-chip is-done">Результат готов</span> : null}
                    </div>
                    <strong>{req.title}</strong>
                    <small>
                      Срок: {req.deadline || '—'} · Бюджет: {req.budget || '—'}
                    </small>
                  </div>
                  <span aria-hidden="true">→</span>
                </button>
              ))
            ) : (
              <div className="client-empty">Заявок пока нет</div>
            )}
          </div>
        </section>

        <aside className="client-side min-w-0">
          <section className="client-panel">
            <div className="client-section-head">
              <div className="client-section-head__title">
                <ConnectionsIcon className="w-4 h-4 text-purple-400" />
                <h2>Персональный менеджер</h2>
              </div>
              <button type="button" className="client-link" onClick={() => handleOpenCard('ACADEMIC_CONNECTIONS')}>
                Контакты →
              </button>
            </div>

            {summary?.connections && summary.connections.length > 0 ? (
              <div className="client-manager">
                <div className="client-manager__row">
                  <div className="client-manager__avatar">ИА</div>
                  <div>
                    <strong>{summary.connections[0].name}</strong>
                    <small>{summary.connections[0].role_or_title || 'Ведущий координатор'}</small>
                  </div>
                </div>
                <div className="client-manager__contacts">
                  <div>Тел: {summary.connections[0].phone || '+7 (701) 444-8822'}</div>
                  <div>Telegram: {summary.connections[0].telegram || '@ibrayev_sc'}</div>
                </div>
                <button
                  type="button"
                  className="client-btn client-btn--violet"
                  onClick={() => handleOpenCard('ACADEMIC_CONNECTIONS')}
                >
                  <MessageSquareIcon className="w-3.5 h-3.5" />
                  Написать сообщение
                </button>
              </div>
            ) : (
              <div className="client-empty">Менеджер ещё не назначен</div>
            )}
          </section>

          <section className="client-panel">
            <div className="client-section-head">
              <div className="client-section-head__title">
                <ArticleIcon className="w-4 h-4 text-orange-400" />
                <h2>Соавторство статей</h2>
              </div>
              <button type="button" className="client-link" onClick={() => handleOpenCard('ARTICLES')}>
                Все ({summary?.articles.length || 0}) →
              </button>
            </div>

            {summary?.articles && summary.articles.length > 0 ? (
              <div className="client-articles">
                {summary.articles.slice(0, 2).map((art) => (
                  <button
                    key={art.id}
                    type="button"
                    className="client-article"
                    onClick={() => handleOpenCard('ARTICLES')}
                  >
                    <div className="client-article__meta">
                      <span>{art.article_code}</span>
                      <span>{art.quartile}</span>
                    </div>
                    <strong>{art.title}</strong>
                    <div className="client-article__progress">
                      <div style={{ width: `${art.progress_percent}%` }} />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="client-empty">Нет соавторских статей</div>
            )}
          </section>
        </aside>
      </div>

      <div className="client-widgets">
        <section className="client-panel">
          <div className="client-section-head">
            <div className="client-section-head__title">
              <NotebookIcon className="w-4 h-4 text-rose-400" />
              <h2>Блокнот</h2>
            </div>
            <button type="button" className="client-link" onClick={() => handleOpenCard('ACADEMIC_NOTEBOOK')}>
              Все записи →
            </button>
          </div>
          <div className="client-list">
            {summary?.notes && summary.notes.length > 0 ? (
              summary.notes.slice(0, 2).map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className="client-list__item"
                  onClick={() => handleOpenCard('ACADEMIC_NOTEBOOK')}
                >
                  <strong>{n.title}</strong>
                  <span>{n.content}</span>
                </button>
              ))
            ) : (
              <div className="client-empty">Заметок пока нет</div>
            )}
          </div>
        </section>

        <section className="client-panel">
          <div className="client-section-head">
            <div className="client-section-head__title">
              <CalendarIcon className="w-4 h-4 text-sky-400" />
              <h2>Ближайшие дедлайны</h2>
            </div>
            <button type="button" className="client-link" onClick={() => handleOpenCard('ACADEMIC_CALENDAR')}>
              Календарь →
            </button>
          </div>
          <div className="client-list">
            {summary?.calendar && summary.calendar.length > 0 ? (
              summary.calendar.slice(0, 3).map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  className="client-list__item"
                  onClick={() => handleOpenCard('ACADEMIC_CALENDAR')}
                >
                  <div className="client-list__meta">
                    <span>{ev.event_type}</span>
                    <span>{ev.start_time.slice(0, 10)}</span>
                  </div>
                  <strong>{ev.title}</strong>
                </button>
              ))
            ) : (
              <div className="client-empty">Событий нет</div>
            )}
          </div>
        </section>

        <section className="client-panel">
          <div className="client-section-head">
            <h2>Быстрые сервисы</h2>
          </div>
          <div className="client-quick">
            <button type="button" onClick={() => handleOpenCard('RESEARCH_TOOLS')}>
              <span>
                <ToolsIcon className="w-4 h-4 text-blue-400" />
                Проверить антиплагиат
              </span>
              <span>→</span>
            </button>
            <button type="button" onClick={() => handleOpenCard('ARTICLES')}>
              <span>
                <ArticleIcon className="w-4 h-4 text-orange-400" />
                Соавторство статей (S10)
              </span>
              <span className="client-chip is-warn">{summary?.articles.length || 0}</span>
            </button>
            <button type="button" onClick={() => handleOpenCard('B5_FILES')}>
              <span>
                <FilesIcon className="w-4 h-4 text-teal-400" />
                Файлы и готовые отчеты
              </span>
              <span>→</span>
            </button>
          </div>
        </section>
      </div>

      <section className="client-support">
        <strong>Академическая поддержка</strong>
        <p>Технический отдел и научный секретариат работают ежедневно с 09:00 до 19:00.</p>
        <a href="mailto:support@zertteu.kz">support@zertteu.kz</a>
      </section>
      </CabinetWorkspaceFrame>
        }
      />

      <Route
        path="requests"
        element={
          <RoleMyRequestsOverlay
            isOpen
            roleContext="CLIENT"
            onClose={backToCabinet}
            onOpenPreviousRequests={summary?.requests.length ? () => navigate(`${routes.client}/requests/previous`) : undefined}
          />
        }
      />
      <Route
        path="requests/previous"
        element={
          <ClientRequestsOverlay
            requests={summary?.requests || []}
            isOpen
            onClose={backToCabinet}
            onOpenCreate={() => setIsCreateModalOpen(true)}
            onAddClarification={handleAddClarification}
          />
        }
      />
      <Route
        path="projects"
        element={
          <ClientProjectsOverlay
            projects={summary?.projects || []}
            isOpen
            onClose={backToCabinet}
            onUpdateStage={handleUpdateStage}
          />
        }
      />
      <Route
        path="calendar"
        element={
          <ClientCalendarOverlay
            events={summary?.calendar || []}
            isOpen
            onClose={backToCabinet}
            onCreateEvent={async (payload) => {
              await createClientCalendarEvent(payload);
              await loadData();
            }}
            onToggleComplete={async (id, isCompleted) => {
              await updateClientCalendarEvent(id, { is_completed: isCompleted });
              await loadData();
            }}
            onDeleteEvent={async (id) => {
              await deleteClientCalendarEvent(id);
              await loadData();
            }}
          />
        }
      />
      <Route
        path="connections"
        element={
          <RoleAcademicConnectionsOverlay
            isOpen
            onClose={backToCabinet}
            roleContext="CLIENT"
            roleTitle="Клиент"
          />
        }
      />
      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="notebook"
        element={
          <ClientNotebookOverlay
            notes={summary?.notes || []}
            isOpen
            onClose={backToCabinet}
            onCreateNote={async (payload) => {
              await createClientNote(payload);
              await loadData();
            }}
            onUpdateNote={async (id, payload) => {
              await updateClientNote(id, payload);
              await loadData();
            }}
            onDeleteNote={async (id) => {
              await deleteClientNote(id);
              await loadData();
            }}
          />
        }
      />
      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="articles"
        element={
          <ClientArticlesOverlay
            articles={summary?.articles || []}
            isOpen
            onClose={backToCabinet}
            onCreateArticle={async (payload) => {
              await createClientJointArticle(payload);
              await loadData();
            }}
            onUpdateArticle={async (id, payload) => {
              await updateClientJointArticle(id, payload);
              await loadData();
            }}
          />
        }
      />
      </Routes>

      <CreateRequestEntry
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          void loadData();
        }}
        activeRole="CLIENT"
        roleLabel="Клиент"
      />

      <SuperAiWorkspaceModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        activeRole="CLIENT"
      />
    </div>
  );
};

export default ClientPage;
